import React, { useEffect, useState } from 'react';

import { format } from 'date-fns';

import MyEventsEmptyState, { VARIANT_EMPTY, VARIANT_FILTERED } from './MyEventsEmptyState';

import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import usePagination from 'Hooks/usePagination';

import ArrowRightIcon from 'Assets/icons/arrow-right.svg';

export default function MyEventsTable(props) {
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [order, setOrder] = useState('asc');

    const pagination = usePagination({ perPage: 10 });

    useEffect(() => {
        (async () => {
            const paginationData = await props.fetchEvents(pagination.perPage, pagination.page, sortBy, order, search);

            if (!paginationData) {
                return;
            }

            pagination.setTo(paginationData.meta.to);
            pagination.setFrom(paginationData.meta.from);
            pagination.setTotal(paginationData.meta.total);
            pagination.setLastPage(paginationData.meta.last_page);
        })();
    }, [pagination.page, sortBy, order, search]);

    const toggleSortBy = (field) => {
        if (field === sortBy) {
            setOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));

            return;
        }

        setSortBy(field);
        setOrder('asc');
    };

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            if (pagination.page !== 1) {
                pagination.setPage(1);
            }

            setSearch(event.target.value);
        }
    };

    const pageChange = (page) => {
        pagination.setPage(page.selected + 1);
    };

    const sortState = (field) => ({
        asc: sortBy === field && order === 'asc',
        desc: sortBy === field && order === 'desc',
    });

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-mb-6">
                <div>
                    <h4 className="table__header">{$t('My Events')}</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination lastPage={pagination.lastPage} pageChange={pageChange} forcePage={pagination.page - 1} />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <TableSearchInput placeholder={$t('Search projects by name')} handleKeyPress={handleSearchKeyPress} />
                </div>
            </div>

            {props.events.length ? (
                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th className="tw-min-w-44" onClick={() => toggleSortBy('name')}>
                                    <TableSortHeaderCell {...sortState('name')}>{$t('Name')}</TableSortHeaderCell>
                                </th>
                                <th className="tw-w-28 tw-min-w-28 tw-max-w-28" onClick={() => toggleSortBy('start_date')}>
                                    <TableSortHeaderCell {...sortState('start_date')}>{$t('Start date')}</TableSortHeaderCell>
                                </th>
                                <th className="tw-w-28 tw-min-w-28 tw-max-w-28" onClick={() => toggleSortBy('end_date')}>
                                    <TableSortHeaderCell {...sortState('end_date')}>{$t('End date')}</TableSortHeaderCell>
                                </th>
                                <th className="tw-w-28 tw-min-w-28 tw-max-w-28" onClick={() => toggleSortBy('currency_code')}>
                                    <TableSortHeaderCell {...sortState('currency_code')}>{$t('Currency')}</TableSortHeaderCell>
                                </th>
                                <th className="tw-w-28 tw-min-w-28 tw-max-w-28" />
                            </tr>
                        </thead>
                        <tbody>
                            {props.events.map((event) => (
                                <MyEventRow key={event.id} event={event} eventDashboardUrl={props.eventDashboardUrl} />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <MyEventsEmptyState variant={search ? VARIANT_FILTERED : VARIANT_EMPTY} />
            )}
        </div>
    );
}

function MyEventRow({ event, eventDashboardUrl }) {
    const dashboardUrl = eventDashboardUrl.replace('EVENT_SLUG', event.slug);

    return (
        <tr>
            <td>
                <a href={dashboardUrl}>{event.name}</a>
            </td>
            <td className="inbud-date-cell">{format(new Date(event.start_date), 'dd-MM-yyyy')}</td>
            <td className="inbud-date-cell">{format(new Date(event.end_date), 'dd-MM-yyyy')}</td>
            <td>{event.currency_code}</td>
            <td>
                <a href={dashboardUrl} className="tw-inline-flex tw-items-center tw-gap-x-1.5">
                    {$t('Open')}
                    <ArrowRightIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                </a>
            </td>
        </tr>
    );
}
